import { buildExternalEmbedUrl, resolveCardVideoActions } from './gvode-video-utils.js';

class GvodeCardVideo extends HTMLElement {
  #hoverTimer;
  #previewTrigger;
  #externalTrigger;
  #video;
  #dialog;
  #player;
  #surface;
  #externalVideo;
  #previousFocus;
  #finePointer = matchMedia('(pointer: fine)');
  #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#previewTrigger = this.querySelector('[data-video-preview-trigger]');
    this.#externalTrigger = this.querySelector('[data-video-external-trigger]');
    this.#video = this.querySelector('video');
    this.#dialog = this.querySelector('[data-video-dialog]');
    this.#player = this.querySelector('[data-video-player]');
    this.#surface = this.closest('.card-gallery');
    const actions = resolveCardVideoActions({
      previewSrc: this.dataset.previewSrc,
      externalUrl: this.dataset.externalUrl,
    });
    this.#externalVideo = actions.externalVideo;
    if (!actions.externalVideo) {
      delete this.dataset.externalUrl;
      this.#externalTrigger?.remove();
      this.#dialog?.remove();
      this.#externalTrigger = null;
      this.#dialog = null;
      this.#player = null;
    }
    if (!actions.preview) {
      this.#previewTrigger?.remove();
      this.#video?.remove();
      this.#previewTrigger = null;
      this.#video = null;
    }
    if (!actions.preview && !actions.externalVideo) {
      this.remove();
      return;
    }
    this.#surface?.addEventListener('pointerenter', this.#onPointerEnter);
    this.#surface?.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('click', this.#onClick);
    this.#video?.addEventListener('error', this.#stopPreview);
    this.#dialog?.addEventListener('close', this.#onDialogClose);
    this.#dialog?.addEventListener('click', this.#onDialogBackdrop);
  }

  disconnectedCallback() {
    clearTimeout(this.#hoverTimer);
    this.#surface?.removeEventListener('pointerenter', this.#onPointerEnter);
    this.#surface?.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('click', this.#onClick);
    this.#video?.removeEventListener('error', this.#stopPreview);
    this.#dialog?.removeEventListener('close', this.#onDialogClose);
    this.#dialog?.removeEventListener('click', this.#onDialogBackdrop);
    this.#stopPreview();
    if (this.#video?.src) {
      this.#video.removeAttribute('src');
      this.#video.load();
    }
    this.#cleanupDialog(false);
  }

  #onPointerEnter = () => {
    if (!this.#finePointer.matches || this.#reducedMotion.matches || !this.dataset.previewSrc) return;
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = setTimeout(this.#playPreview, 250);
  };

  #onPointerLeave = () => {
    clearTimeout(this.#hoverTimer);
    this.#stopPreview();
  };

  #playPreview = async () => {
    if (!(this.#video instanceof HTMLVideoElement) || !this.dataset.previewSrc) return;
    if (!this.#video.src) this.#video.src = this.dataset.previewSrc;
    this.#video.hidden = false;
    try {
      await this.#video.play();
      this.#setPreviewState(true);
    } catch {
      this.#stopPreview();
    }
  };

  #stopPreview = () => {
    if (!(this.#video instanceof HTMLVideoElement)) return;
    this.#video.pause();
    this.#video.currentTime = 0;
    this.#video.hidden = true;
    this.#setPreviewState(false);
  };

  #setPreviewState(playing) {
    if (!(this.#previewTrigger instanceof HTMLButtonElement)) return;
    this.#previewTrigger.setAttribute('aria-pressed', String(playing));
    this.#previewTrigger.setAttribute(
      'aria-label',
      playing ? this.#previewTrigger.dataset.previewPauseLabel : this.#previewTrigger.dataset.previewPlayLabel,
    );
    const label = this.#previewTrigger.querySelector('[data-preview-label]');
    if (label) label.textContent = playing ? 'Pause preview' : 'Preview';
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-video-close]')) {
      event.preventDefault();
      event.stopPropagation();
      this.#dialog?.close();
      return;
    }
    if (target?.closest('[data-video-preview-trigger]')) {
      event.preventDefault();
      event.stopPropagation();
      this.#video?.paused ? this.#playPreview() : this.#stopPreview();
      return;
    }
    if (target?.closest('[data-video-external-trigger]')) {
      event.preventDefault();
      event.stopPropagation();
      this.#openExternalVideo();
    }
  };

  #openExternalVideo() {
    const src = buildExternalEmbedUrl(this.#externalVideo);
    if (!src || !(this.#dialog instanceof HTMLDialogElement) || !(this.#player instanceof HTMLElement)) return;
    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = 'Product video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    this.#stopPreview();
    this.#player.replaceChildren(iframe);
    this.#previousFocus = document.activeElement;
    this.#dialog.showModal();
    this.#dialog.querySelector('[data-video-close]')?.focus();
  }

  #onDialogBackdrop = (event) => {
    if (event.target === this.#dialog) this.#dialog.close();
  };

  #onDialogClose = () => {
    this.#cleanupDialog(true);
  };

  #cleanupDialog(restoreFocus) {
    this.#player?.replaceChildren();
    if (restoreFocus && this.#previousFocus instanceof HTMLElement) this.#previousFocus.focus();
    this.#previousFocus = null;
  }
}

if (!customElements.get('gvode-card-video')) customElements.define('gvode-card-video', GvodeCardVideo);
